/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Form rules for geofences and categories. The name rules are Refresh's
 * (`refresh/app/validators/input.js`, `refresh/server/api/lib/validators/geofence-name.js`), so a
 * name saved here is one Refresh accepts too; api-insights checks the same pattern.
 */

import { areaSquareMeters, isSelfIntersecting, ringVertices } from './geometry'
import type { GeofenceShape } from './types'

export const NAME_MAX_LENGTH = 40

const GEOFENCE_NAME_PATTERN = /^[a-zA-Z0-9_\-\s.&()+]+$/

/** Above this a geofence is almost always a mis-click: warned about, not refused. */
export const LARGE_AREA_SQUARE_METERS = 10_000 * 1_000_000

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

/**
 * The first problem with a geofence name, or undefined. `otherNames` are the company's other
 * geofences (the one being edited left out); a match ignores case and surrounding spaces.
 */
export const validateGeofenceName = (name: string, otherNames: string[]): string | undefined => {
  const trimmed = name.trim()
  if (!trimmed) return 'Enter a name'
  if (!GEOFENCE_NAME_PATTERN.test(trimmed)) {
    return 'Use letters, numbers, spaces and . _ - ( ) & + only'
  }
  if (trimmed.length > NAME_MAX_LENGTH) return `Use ${NAME_MAX_LENGTH} characters or fewer`
  if (otherNames.some((other) => sameName(other, trimmed))) return `A geofence called “${trimmed}” already exists`
  return undefined
}

export const validateCategoryName = (name: string, otherNames: string[]): string | undefined => {
  const trimmed = name.trim()
  if (!trimmed) return 'Enter a name'
  if (trimmed.length > NAME_MAX_LENGTH) return `Use ${NAME_MAX_LENGTH} characters or fewer`
  if (otherNames.some((other) => sameName(other, trimmed))) return `A category called “${trimmed}” already exists`
  return undefined
}

export const validateShape = (shape: GeofenceShape | null): string | undefined => {
  if (!shape) return 'Draw the geofence on the map'
  if (ringVertices(shape.ring).length < 3) return 'A geofence needs at least 3 points'
  if (shape.type === 'polygon' && isSelfIntersecting(shape.ring)) return 'The outline crosses itself'
  return undefined
}

export const isLargeShape = (shape: GeofenceShape | null): boolean =>
  !!shape && areaSquareMeters(shape.ring) > LARGE_AREA_SQUARE_METERS
